import { apply, bump, counter, onceToday, toast } from '../core/effects';
import { bus } from '../core/bus';
import { S } from '../core/game';
import { saveGame } from '../core/save';
import { CHORES, dayId, DAY_ORDER, XiaoyuPhase } from '../core/state';
import { announceFestival } from './festivals';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { say } from '../ui/dialog';
import { showSummary, type DaySummary } from '../ui/summary';
import { DAY_SPAWN, REST_SPOTS } from '../world/map';
import type { World } from '../world/world';
import { DAYS, FORCE_SLEEP, todaysObjectives } from './days';

let world: World | null = null;
let ending = false;
let collapsing = false;

export function bindWorld(w: World): void {
  world = w;
}

const HOTEL_WAKE = { map: 'hotel' as const, x: 9 * 16 + 8, y: 4 * 16 + 8 };

/** Reset the per-day state and put the player where the day starts. */
export function startDay(): void {
  const s = S();
  const def = DAYS[dayId(s)];
  s.time = def.start;
  s.daily = {};
  s.counters = { starsAtStart: s.stars };
  for (const c of CHORES) s.chores[c] = false;
  s.xiaoyu = { phase: XiaoyuPhase.Absent, x: 180, y: 600 };
  s.stats.energy = Math.max(s.stats.energy, 80);
  s.stats.bladder = 15;
  // A night at the hotel: wake up there, with the return ticket still valid.
  const hotelNight = !!s.forever[`hotel.${s.dayIndex - 1}`];
  if (hotelNight) s.daily.ticketHualien = true;
  const spawn = hotelNight ? HOTEL_WAKE : DAY_SPAWN[def.place];
  s.player.map = spawn.map;
  s.player.x = spawn.x;
  s.player.y = spawn.y;
  world?.teleport(spawn.map, spawn.x, spawn.y);
  ending = false;
  const d = m().days[dayId(s)];
  toast(`${m().hud.week(s.week)} · ${d.name} · ${d.kind}`);
  announceFestival();
  bus.emit('dayStart');
  saveGame(s);
  bus.emit('change');
}

/** Restore the world after loading a save mid-day. */
export function resumeDay(): void {
  const s = S();
  world?.teleport(s.player.map, s.player.x, s.player.y);
  ending = false;
  bus.emit('change');
}

export function endDay(): void {
  if (ending) return;
  ending = true;
  const s = S();
  const goals = todaysObjectives(s);
  const summary: DaySummary = {
    day: m().days[dayId(s)].name,
    stars: s.stars - (s.counters.starsAtStart ?? s.stars),
    goalsDone: goals.filter((g) => g.done).length,
    goalsTotal: goals.length,
    choresDone: CHORES.filter((c) => s.chores[c]).length,
    choresTotal: CHORES.length,
    collapses: counter('collapses'),
    mood: Math.round(s.stats.mood),
    momLove: Math.round(s.affection.mom),
    weekEnd: (s.dayIndex + 1) % DAY_ORDER.length === 0,
    week: s.week,
    totalStars: s.stars,
  };
  sfx('fanfare');
  showSummary(summary, () => {
    s.dayIndex++;
    if (s.dayIndex % DAY_ORDER.length === 0) s.week++;
    s.stats.energy = 90;
    s.stats.mood = Math.max(50, s.stats.mood);
    startDay();
  });
}

/** Per-frame rules: tiredness, bathroom, bedtime. */
export function needsTick(): void {
  const s = S();
  if (!world || ending) return;
  const st = s.stats;

  if (st.energy <= 0 && !collapsing) collapse();

  if (st.bladder >= 100) {
    sfx('bad');
    st.bladder = 0;
    s.daily['warn.bladder'] = false;
    apply({ mood: -25 });
    bump('accidents');
    say({ who: m().common.thoughts, icon: '😳', text: m().status.accident });
  } else if (st.bladder >= 80 && !s.daily['warn.bladder']) {
    s.daily['warn.bladder'] = true;
    toast(`🚻 ${m().status.bladderWarn}`);
  }

  if (st.energy < 15 && !s.daily['warn.energy']) {
    s.daily['warn.energy'] = true;
    toast(`😵 ${m().status.tired}`);
  } else if (st.energy > 30) s.daily['warn.energy'] = false;

  if (s.time >= 21 * 60 && onceToday('lateHint')) toast(`🌙 ${m().status.lateNight}`);
  if (s.time >= FORCE_SLEEP) endDay();
}

function collapse(): void {
  if (!world) return;
  collapsing = true;
  const w = world;
  w.cancelMove();
  w.player.down = true;
  sfx('bad');
  window.setTimeout(() => {
    say({
      who: m().status.collapseTitle,
      icon: '😵',
      text: m().status.collapseText,
      choices: [
        {
          text: m().common.ok,
          run: () => {
            const s = S();
            const spot = w.map.id === 'school' ? REST_SPOTS.school : REST_SPOTS.home;
            w.player.down = false;
            w.teleport(spot.map, spot.x, spot.y);
            s.stats.energy = 40;
            apply({ mood: -15, minutes: 120 }, { float: false });
            bump('collapses');
            collapsing = false;
          },
        },
      ],
    });
  }, 900);
}
